import React, { useState, useEffect } from 'react';
import { useParams } from "react-router-dom";
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader } from "../ui";
import { unifiedContactService } from '../../services/unifiedContactService';
import type { UnifiedContact } from '../../services/unifiedContactService';
import { useEFrameData } from "./hooks/useEFrameData";
import { getPlanOwnerName } from "../../utils/planOwnerLabels";

// Icon used for people-related stats and contact rows
const PeopleIcon = ({ className = "" }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle
      cx="9.5"
      cy="7"
      r="4"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M20 21v-2a4 4 0 0 0-3-3.87"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16 3.13a4 4 0 0 1 0 7.75"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Icon used for email-related stats and contact metadata
const EmailIcon = ({ className = "" }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 6h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="m22 8-10 7L2 8"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Icon used for phone-related stats and contact metadata
const PhoneIcon = ({ className = "" }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.78.63 2.62a2 2 0 0 1-.45 2.11L8 9.91a16 16 0 0 0 6.09 6.09l1.46-1.29a2 2 0 0 1 2.11-.45c.84.3 1.72.51 2.62.63A2 2 0 0 1 22 16.92Z"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EFrame142: React.FC = () => {
  const { estateId } = useParams<{ estateId: string }>();
  const { session } = useEFrameData(estateId ?? null);
  const planOwnerName = getPlanOwnerName(session.name);
  const [allContacts, setAllContacts] = useState<UnifiedContact[]>([]);
  const [loading, setLoading] = useState(true);

  // Loads the estate owner's contact list for read-only display
  useEffect(() => {
    if (!estateId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    unifiedContactService.getContactsForEstate(estateId)
      .then((contacts) => { if (!cancelled) setAllContacts(contacts); })
      .catch((error) => { console.error('[EFrame142] Error loading contacts:', error); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [estateId]);

  // Summary cards shown above the contact list
  const statCards = [
    {
      label: 'Total People',
      value: allContacts.length,
      icon: <PeopleIcon className="w-6 h-6 text-cyan-700" />,
    },
    {
      label: 'With Email',
      value: allContacts.filter(c => c.email?.trim()).length,
      icon: <EmailIcon className="w-6 h-6 text-cyan-700" />,
    },
    {
      label: 'With Phone',
      value: allContacts.filter(c => c.phone?.trim()).length,
      icon: <PhoneIcon className="w-6 h-6 text-cyan-700" />,
    },
  ];

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <div className="w-full max-w-[835px]">

          {/* Page header and primary action */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            <div className="flex-1">
              <ResponsiveHeader title={`${planOwnerName}'s Trusted Circle`} />
            </div>
          </div>

          {/* Intro copy for the trusted circle section */}
          <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
            View the network of family and professionals associated with {planOwnerName}'s legacy. 
            This list helps you identify who is involved and their designated roles in the estate. 
          </p>

          {/* Summary stats for the current contact list */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {statCards.map((card) => (
              <div
                key={card.label}
                className="bg-white rounded-md border border-stone-200 p-6"
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0">{card.icon}</div>

                  <div className="flex flex-col">
                    <div className="font-sans text-base text-stone-600">
                      {card.label}
                    </div>

                    <div className="font-sans text-3xl font-medium text-stone-800 leading-none mt-4">
                      {loading ? '—' : card.value}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Full read-only contact list */}
          <div className="mt-7">
            <div className="flex items-center gap-3 mb-3">
              <h2 className="font-sans text-xl font-medium text-stone-800">
                All Contacts
              </h2>
            </div>

            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-12 text-stone-500">
                  <div className="inline-block w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin mb-2" />
                  <p className="font-sans text-sm">Loading contacts...</p>
                </div>
              ) : allContacts.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-stone-300 rounded-lg">
                  <p className="font-sans text-stone-500 text-sm">No contacts have been added yet.</p>
                </div>
              ) : (
                allContacts.map((contact) => {
                  const relationshipText = contact.relationship
                    ? contact.relationship.charAt(0).toUpperCase() + contact.relationship.slice(1)
                    : null;

                  return (
                    <div
                      key={contact.id}
                      className="flex flex-col sm:flex-row sm:items-start gap-4 bg-white rounded-lg border border-stone-200 p-6"
                    >
                      {/* Contact icon and main details */}
                      <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 flex-1 min-w-0">
                        <PeopleIcon className="w-6 h-6 text-cyan-700 flex-shrink-0 mt-0.5" />

                        <div className="flex-1 min-w-0">
                          <div className="font-sans text-lg font-medium text-stone-800">
                            {contact.firstName} {contact.lastName}
                          </div>

                          {relationshipText && (
                            <div className="font-sans text-base text-stone-600 mt-1">
                              {relationshipText}
                            </div>
                          )}

                          {(contact.email || contact.phone) && (
                            <div className="flex flex-wrap gap-x-5 gap-y-2 font-sans text-base text-stone-600 mt-2">
                              {contact.email && (
                                <div className="flex items-center gap-2 min-w-0">
                                  <EmailIcon className="w-4 h-4 text-cyan-700 flex-shrink-0" />
                                  <span className="break-all">{contact.email}</span>
                                </div>
                              )}

                              {contact.phone && (
                                <div className="flex items-center gap-2 min-w-0">
                                  <PhoneIcon className="w-4 h-4 text-cyan-700 flex-shrink-0" />
                                  <span>{contact.phone}</span>
                                </div>
                              )}
                            </div>
                          )}

                          {contact.notes && (
                            <div className="font-sans text-sm text-stone-500 mt-2">
                              {contact.notes}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default EFrame142;